import type { DayOfWeek } from "@/types/meal-plan-state";

/**
 * Week URLs, mine or a friend's (friend mode):
 *   /week/:weekId[/:sub]                    my weeks
 *   /friend/:friendId/week/:weekId[/:sub]   an accepted friend's weeks
 * `sub` is "" (week index), "comidas", "shopping" or a day ("mon".."sun").
 */
export interface WeekPathParts {
  /** null for my own weeks. */
  friendId: number | null;
  weekId: string;
  sub: string;
}

const SELF_RE = /^\/week\/([^/]+)(?:\/([^/]+))?\/?$/;
const FRIEND_RE = /^\/friend\/([1-9]\d*)\/week\/([^/]+)(?:\/([^/]+))?\/?$/;

/** null for a malformed escape (a hand-typed "%"): the navbar renders on every path. */
function decodeSegment(segment: string): string | null {
  try {
    return decodeURIComponent(segment);
  } catch {
    return null;
  }
}

export function parseWeekPath(pathname: string): WeekPathParts | null {
  const self = pathname.match(SELF_RE);
  if (self) {
    const weekId = decodeSegment(self[1]!);
    return weekId === null ? null : { friendId: null, weekId, sub: self[2] ?? "" };
  }
  const friend = pathname.match(FRIEND_RE);
  if (friend) {
    const friendId = Number(friend[1]);
    const weekId = decodeSegment(friend[2]!);
    if (!Number.isSafeInteger(friendId) || weekId === null) return null;
    return { friendId, weekId, sub: friend[3] ?? "" };
  }
  return null;
}

/** "/week" or "/friend/:id/week". */
export function weekPathPrefix(friendId: number | null): string {
  return friendId === null ? "/week" : `/friend/${friendId}/week`;
}

export function weekPath(friendId: number | null, weekId: string, sub = ""): string {
  const base = `${weekPathPrefix(friendId)}/${weekId}`;
  return sub ? `${base}/${sub}` : base;
}

/**
 * Where picking `target` (null: me) in the person switcher goes from
 * `pathname`: the same week and sub-page, so "Romina · jueves" becomes
 * "David · jueves". Compras is always mine: from it, switching to a friend
 * opens their week (its index picks the day) and switching to me stays in
 * my Compras. Outside the week screens it goes to today in the current week.
 */
export function switchPersonPath(
  pathname: string,
  target: number | null,
  today: { weekId: string; day: DayOfWeek },
): string {
  const parsed = parseWeekPath(pathname);
  if (!parsed) return weekPath(target, today.weekId, today.day);
  if (parsed.sub === "shopping") {
    return target === null ? weekPath(null, parsed.weekId, "shopping") : weekPath(target, parsed.weekId);
  }
  return weekPath(target, parsed.weekId, parsed.sub);
}
